import { Suspense } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDirection } from '@/hooks/useDirection';
import { useAuthBootstrap } from '@/hooks/useAuth';
import { useThemeMode } from '@/hooks/useThemeMode';
import { useAuthStore } from '@/stores/auth.store';
import { Welcome } from '@/screens/Welcome';
import { VerifyOtp } from '@/screens/VerifyOtp';
import { IosInstallBanner } from '@/components/IosInstallBanner';
import { FcmTenantSync } from '@/components/FcmTenantSync';
import { BottomNav } from '@/components/BottomNav';
import { lazyRoute } from '@/lib/lazy-route';

/**
 * Route-level code splitting.
 *
 * The whole app shipped as ONE ~1.7 MB chunk, and maplibre-gl (the single
 * biggest dependency) was in it — so every first-time visitor downloaded
 * the map engine before they could read the welcome screen, on Canadian
 * mobile data. Each screen is now its own chunk, loaded on navigation.
 *
 * Welcome + VerifyOtp stay EAGER: they are the first paint for a
 * signed-out user, so deferring them would only add a spinner to the
 * critical path.
 */
const Home = lazyRoute(() => import('@/screens/Home').then((m) => ({ default: m.Home })));
const ReportIssue = lazyRoute(() =>
  import('@/screens/ReportIssue').then((m) => ({ default: m.ReportIssue })),
);
const MyReports = lazyRoute(() =>
  import('@/screens/MyReports').then((m) => ({ default: m.MyReports })),
);
const ReportDetail = lazyRoute(() =>
  import('@/screens/ReportDetail').then((m) => ({ default: m.ReportDetail })),
);
const Announcements = lazyRoute(() =>
  import('@/screens/Announcements').then((m) => ({ default: m.Announcements })),
);
const AnnouncementDetail = lazyRoute(() =>
  import('@/screens/AnnouncementDetail').then((m) => ({ default: m.AnnouncementDetail })),
);
const ProjectDetail = lazyRoute(() =>
  import('@/screens/ProjectDetail').then((m) => ({ default: m.ProjectDetail })),
);
const PollDetail = lazyRoute(() =>
  import('@/screens/PollDetail').then((m) => ({ default: m.PollDetail })),
);
const Notifications = lazyRoute(() =>
  import('@/screens/Notifications').then((m) => ({ default: m.Notifications })),
);
const Profile = lazyRoute(() => import('@/screens/Profile').then((m) => ({ default: m.Profile })));
const MapView = lazyRoute(() => import('@/screens/MapView').then((m) => ({ default: m.MapView })));
const Support = lazyRoute(() => import('@/screens/Support').then((m) => ({ default: m.Support })));
const SupportNewTicket = lazyRoute(() =>
  import('@/screens/SupportNewTicket').then((m) => ({ default: m.SupportNewTicket })),
);
const SupportTicket = lazyRoute(() =>
  import('@/screens/SupportTicket').then((m) => ({ default: m.SupportTicket })),
);

/**
 * App router with three auth states:
 *   - `initializing` — splash while we wait for Firebase to fire its
 *     first onAuthStateChanged callback.
 *   - `signed-out` → Welcome screen + OTP verify route.
 *   - `signed-in`  → 4-tab layout (Home / Map / My Reports / Profile,
 *     mvp-v1 §18) + back-header detail routes outside the tab shell.
 */
export function App(): React.JSX.Element {
  useDirection();
  useAuthBootstrap();
  // Light / dark / system theme bootstrap (spec §14)
  useThemeMode();

  const status = useAuthStore((s) => s.status);

  if (status === 'initializing') return <Splash />;

  return (
    <>
      {/* One boundary around the whole route tree: a lazy screen resolves in
          a few ms from the Workbox precache, and reusing the same Splash the
          app already shows during auth keeps the transition visually
          identical instead of introducing a second loading style. */}
      <Suspense fallback={<Splash />}>
        <Routes>
          {status === 'signed-out' ? (
            <>
              <Route path="/" element={<Welcome />} />
              <Route path="/verify" element={<VerifyOtp />} />
              <Route path="*" element={<NavigateToWelcome />} />
            </>
          ) : (
            <>
              {/* Root tabs — persistent bottom nav with docked center action */}
              <Route element={<TabLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/map" element={<MapView />} />
                <Route path="/reports" element={<MyReports />} />
                <Route path="/profile" element={<Profile />} />
              </Route>
              {/* Back-header screens outside the tab shell */}
              <Route path="/report" element={<ReportIssue />} />
              <Route path="/reports/:id" element={<ReportDetail />} />
              <Route path="/announcements" element={<Announcements />} />
              <Route path="/announcements/:id" element={<AnnouncementDetail />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/polls/:id" element={<PollDetail />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/support" element={<Support />} />
              <Route path="/support/new" element={<SupportNewTicket />} />
              <Route path="/support/:id" element={<SupportTicket />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </Suspense>
      {/* Device ↔ selected-city FCM topic sync (announcements/emergency). */}
      {status !== 'signed-out' && <FcmTenantSync />}
      {/* iOS Safari can't trigger the standard install prompt; show a
          banner walking the user through the share-sheet route so they
          can enable web push (iOS 16.4+ requires Home Screen install). */}
      <IosInstallBanner />
    </>
  );
}

/**
 * Signed-in shell for the four root screens. The "+ Report Issue"
 * action is DOCKED in the center of the bottom nav (final-design
 * blueprint) — always visible, never floating over content.
 */
function TabLayout(): React.JSX.Element {
  return (
    <>
      <Outlet />
      <BottomNav />
    </>
  );
}

function Splash(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <main className="flex min-h-dvh items-center justify-center bg-surface">
      <img src="/brand/mycity-mark.svg" alt="My City" className="h-16 w-16 animate-pulse" />
      <span className="sr-only">{t('common.loading')}</span>
    </main>
  );
}

/** Send any unknown route to / while signed-out, preserving the requested path. */
function NavigateToWelcome(): React.JSX.Element {
  const loc = useLocation();
  return <Navigate to="/" replace state={{ from: loc.pathname }} />;
}
